import React, { useState, useEffect } from 'react';
import { LoginPage } from './components/LoginPage';
import { Dashboard } from './components/Dashboard';
import { ServerDetail } from './components/ServerDetail';
import { UserManagement } from './components/UserManagement';
import { Server, FirewallRule, UserRole } from './types';
import { Icons } from './components/Icons';

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>('user');
  const [userId, setUserId] = useState<string>('');
  
  const [currentView, setCurrentView] = useState<'dashboard' | 'users'>('dashboard');
  const [selectedServer, setSelectedServer] = useState<Server | null>(null);
  
  const [targetUserId, setTargetUserId] = useState<string | null>(null);

  const [servers, setServers] = useState<Server[]>([]);
  const [loadingServers, setLoadingServers] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role') as UserRole;
    const uid = localStorage.getItem('user_id');
    if (token && uid) {
        setIsAuthenticated(true);
        if(role) setUserRole(role);
        setUserId(uid);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
        loadServers();
    }
  }, [isAuthenticated, targetUserId]); 

  const getAuthHeaders = () => {
      return {
          auth_user_id: userId,
          auth_role: userRole
      };
  };

  const loadServers = async () => {
      setLoadingServers(true);
      try {
          const params = new URLSearchParams({ 
              action: 'list_servers',
              auth_user_id: userId,
              auth_role: userRole
          });
          
          if (targetUserId) {
              params.append('target_user_id', targetUserId);
          }

          const res = await fetch(`/api/backend.php?${params.toString()}`);
          const data = await res.json();
          if (data.success) {
              setServers(data.servers);
          }
      } catch (e) {
          console.error("API Hatası", e);
      } finally {
          setLoadingServers(false);
      }
  };

  const handleLogin = (role: UserRole, uid: string) => {
    setIsAuthenticated(true);
    setUserRole(role);
    setUserId(uid);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setSelectedServer(null);
    setCurrentView('dashboard');
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('user_id');
  };

  const handleAddServer = async (newServerData: any) => {
    try {
        const res = await fetch('/api/backend.php', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({ 
                action: 'add_server', 
                ...newServerData,
                ...getAuthHeaders() 
            })
        });
        const data = await res.json();
        if (data.success) {
            loadServers(); 
        } else {
            alert("Ekleme başarısız: " + data.message);
        }
    } catch (e) { alert("API Hatası"); }
  };

  const handleDeleteServer = async (serverId: string) => {
      try {
          const res = await fetch('/api/backend.php', {
              method: 'POST',
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({
                  action: 'delete_server',
                  server_id: serverId,
                  ...getAuthHeaders()
              })
          });
          const data = await res.json();
          if(data.success) {
              loadServers();
          } else {
              alert("Hata: " + data.message);
          }
      } catch(e) { alert("Bağlantı hatası"); }
  };

  const handleViewUserServers = (targetUid: string) => {
      setTargetUserId(targetUid);
      setCurrentView('dashboard'); 
  };

  const handleClearFilter = () => {
      setTargetUserId(null); 
      loadServers();
  };

  const handleUpdateRules = (serverId: string, newRules: FirewallRule[]) => {
    setServers(prev => prev.map(s => s.id === serverId ? { ...s, rules: newRules } : s));
    if (selectedServer?.id === serverId) setSelectedServer(prev => prev ? { ...prev, rules: newRules } : null);
  };

  if (!isAuthenticated) return <LoginPage onLogin={handleLogin} />;
  if (selectedServer) return <ServerDetail server={selectedServer} onBack={() => setSelectedServer(null)} onUpdateRules={handleUpdateRules} authHeaders={getAuthHeaders()} />;

  const renderSidebar = () => (
    <aside className="w-64 bg-gray-900 border-r border-gray-800 hidden md:flex flex-col">
        <div className="p-6 border-b border-gray-800">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600/20 rounded-lg"><Icons.Shield className="w-6 h-6 text-blue-500" /></div>
                <span className="text-xl font-bold tracking-tight">VpsGuard</span>
            </div>
        </div>
        <nav className="flex-1 p-4 space-y-2">
            <button onClick={() => { setCurrentView('dashboard'); if(targetUserId) handleClearFilter(); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${currentView === 'dashboard' ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'}`}>
                <Icons.Server className="w-5 h-5" />
                <span>Sunucularım</span>
            </button>
            {userRole === 'admin' && (
                <button onClick={() => setCurrentView('users')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${currentView === 'users' ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'}`}>
                    <Icons.Users className="w-5 h-5" />
                    <span>Kullanıcı Yönetimi</span>
                </button>
            )}
        </nav>
        <div className="p-4 border-t border-gray-800">
            <div className="px-4 py-2 mb-2 text-xs text-gray-500 flex items-center justify-between">
                <span>Rol:</span> <span className="uppercase font-bold text-blue-400">{userRole}</span>
            </div>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors">
                <Icons.LogOut className="w-5 h-5" /> <span>Çıkış Yap</span>
            </button>
        </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-gray-950">
        {currentView !== 'dashboard' && renderSidebar()}
        {currentView === 'dashboard' && !selectedServer && renderSidebar()} 
        
        <main className="flex-1 overflow-auto">
            {currentView === 'dashboard' && (
                <Dashboard 
                    servers={servers} 
                    onSelectServer={setSelectedServer} 
                    onAddServer={handleAddServer}
                    onLogout={handleLogout}
                    onViewDatabase={() => {}}
                    onViewUsers={() => setCurrentView('users')}
                    userRole={userRole}
                    targetUserId={targetUserId}
                    onClearFilter={handleClearFilter}
                    onDeleteServer={handleDeleteServer}
                />
            )}
            {currentView === 'users' && (
                <UserManagement 
                    authHeaders={getAuthHeaders()} 
                    onViewUserServers={handleViewUserServers} 
                />
            )}
        </main>
    </div>
  );
};

export default App;